/* eslint-disable functional/immutable-data */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ViewProps } from 'react-native';
import type { LDClient, LDUser } from 'launchdarkly-js-client-sdk';
import { logger as LOG } from '@sg/garnish';

import type { getAppNavigationContainerRef } from '../../navigation/AppNavigation.ref';
import { initLaunchDarkly } from './client';
import { featureFlagDefaults as defaults } from './defaults';
import { useOverrides } from './LaunchDarklyProvider.overrides';
import type { FeatureFlagOverrides, FeatureFlags } from './types';

// ────────────────────────────────────────────────────────────────────────────────
// LOG.enable('LAUNCHDARKLY');
const logger = LOG.extend('LAUNCHDARKLY');
// ────────────────────────────────────────────────────────────────────────────────

const LaunchDarklyContext = createContext<LaunchDarklyContextType | undefined>(
  undefined,
);

/**
 * Launch Darkly (@see https://www.launchdarkly.com/) provider component.
 */
export const LaunchDarklyProvider = (props: Pick<ViewProps, 'children'>) => {
  const clientRef = useRef<LDClient>();

  const [isReady, setIsReady] = useState(false);
  const { overrides, loadOverrides } = useOverrides();

  // ─────────────────────────────────────────────────────────────────

  const init = useCallback(async (customer?: Customer) => {
    const user = customer ? mapCustomerToLaunchDarklyUser(customer) : undefined;
    const ldClient = await initLaunchDarkly(user);

    // ─────────────────────────────────────────────────────────────────

    if (!ldClient) {
      logger.warn('Could not initialize LaunchDarkly Provider');

      return;
    }

    clientRef.current = ldClient;

    logger.debug('Initialized LaunchDarkly Provider');

    await ldClient.waitUntilReady();

    setIsReady(true);

    logger.debug('LaunchDarkly Provider is ready');
  }, []);

  const syncCustomer = useCallback(async (customer: Customer) => {
    const { current: client } = clientRef;

    if (!client) return;

    const currentUser = client.getUser();
    const isUserAlreadySignedIn = currentUser.email === customer?.email;

    if (isUserAlreadySignedIn) return;

    await client.identify?.(mapCustomerToLaunchDarklyUser(customer));
  }, []);

  const resetCustomer = useCallback(async () => {
    const { current: client } = clientRef;

    if (!client) return;

    await client.identify?.({ anonymous: true });
  }, []);

  const destroy = useCallback(async () => {
    await clientRef.current?.close?.();
  }, []);

  // ────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      clientRef,
      isReady,
      overrides,
      defaults,
      init,
      syncCustomer,
      resetCustomer,
      destroy,
      loadOverrides,
    }),
    [
      isReady,
      overrides,
      init,
      syncCustomer,
      resetCustomer,
      destroy,
      loadOverrides,
    ],
  );

  return (
    <LaunchDarklyContext.Provider value={value}>
      {props.children}
    </LaunchDarklyContext.Provider>
  );
};

/**
 * Launch Darkly (@see https://www.launchdarkly.com/) context hook.
 *
 * @example
 * const { client } = useLaunchDarkly();
 * const featureFlags = client?.allFlags();
 */
export const useLaunchDarkly = () => {
  const context = useContext(LaunchDarklyContext);

  if (context === undefined) {
    throw new Error(
      'useLaunchDarkly must be used within a <LaunchDarklyProvider>',
    );
  }

  return context;
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const mapCustomerToLaunchDarklyUser = (customer: Customer): LDUser => ({
  key: customer.id,
  name: `${customer.firstName} ${customer.lastName}`,
  email: customer.email ?? undefined,
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LaunchDarklyContextType = {
  readonly clientRef: React.RefObject<LDClient | undefined>;
  readonly overrides: FeatureFlagOverrides;
  readonly defaults: FeatureFlags;
  readonly isReady: boolean;
  readonly init: (customer?: Customer) => Promise<void>;
  readonly syncCustomer: (customer: Customer) => Promise<void>;
  readonly resetCustomer: () => Promise<void>;
  readonly destroy: () => Promise<void>;
  readonly loadOverrides: (
    navigationContainerRef: ReturnType<typeof getAppNavigationContainerRef>,
  ) => void;
};

type Customer = {
  readonly id?: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly email?: string | null;
};
